import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { passwordResetApi } from '../lib/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError('');
    setSubmitting(true);
    try {
      await passwordResetApi.request(email);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center gap-2 mb-6">
            <button onClick={() => navigate('/login')} className="text-gray-400 hover:text-[#1f1f1f]" data-testid="back-to-login">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 style={{ fontFamily: 'Poppins, sans-serif' }} className="text-2xl font-bold text-[#1f1f1f]">
              Reset Password
            </h2>
          </div>

          {!submitted ? (
            <>
              <p className="text-sm text-gray-500 mb-6">
                Enter your email address below. If your account exists, an administrator will be notified to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="forgot-error">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">EMAIL ADDRESS</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white font-medium"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? 'Submitting…' : 'Request Password Reset'}
                </Button>
                {error && <p className="text-sm text-red-600 text-center" data-testid="forgot-error">{error}</p>}
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-success">
              <ShieldCheck className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#1f1f1f] mb-2">Request Submitted</h3>
              <p className="text-sm text-gray-500 mb-4">
                If an account with <span className="font-medium text-[#1f1f1f]">{email}</span> exists, a system administrator has been notified. They will reset your password and provide you with a temporary one.
              </p>
              <p className="text-xs text-gray-400 mb-6">
                Please contact your administrator directly for urgent access.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="back-to-login-button"
              >
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
